import { GoogleMap, useLoadScript, MarkerF, InfoWindow } from "@react-google-maps/api";
import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { addMapParam } from "@/redux/SRP/propertiesSlice";
import FavoriteModal from "./FavoriteModal";
import CardItemMap from "./CardItemMap";
import { setSelectedProperty } from "@/redux/SRP/srpSlice";
import map_placeholder from '../../../public/assets/images/srp/map_placeholder.png';
import transparent from '../../../public/assets/images/srp/transparent.png';
import MapClusters from "./MapClusters";
import { trackButtonClick } from "@/utils/analytics";
import { getSrpLocationLabel } from "@/utils/filterTrackingService";
import Cookies from "js-cookie";

const containerStyle = {
  backgroundColor: "#ffffff",
  width: '100%',
  height: "100%",
  position: 'fixed'
};

export default function Map({gooleMapKey}) {
  const dispatch = useDispatch();
  const mapRef= useRef(null);
  const [propertyData, setPropertyData] = useState([]);
  const [prevZoom, setPrevZoom] = useState('');
  const [zoomValue, setZoomValue] = useState('');
  const [mapParams, setMapParams] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [mapTypeId, setMapTypeId] = useState('roadmap');
  const [isFullScreen, setIsFullScreen] = useState(false);
  const {srpTupples: srpTupplesMap} = useSelector((state) => state.srp.srpMapData);
  const {srpTupples} = useSelector((state) => state.srp.srpInitData);
  const {selectedProperty, isSRPMapLoading} = useSelector((state) => state.srp);
  const {mapParam: mapParamRDX} = useSelector((state) => state.properties);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: gooleMapKey,
  });

  const trackMapEvent = (eventName, details, eventParams) => {
    trackButtonClick({
      pageName: eventName,
      eventSource: "Map Interaction",
      eventCategory: "NEW-SRP",
      eventLabel: `${details} | Location: ${getSrpLocationLabel()}`,
      ...(eventParams ? { eventParams } : {}),
    });
  };

  const getPropertiesInViewProt = () => {
    if (!mapRef?.current || !srpTupples) return 0;

    const bounds = mapRef?.current?.getBounds();
    if (!bounds) return 0;

    // Count properties that fall within the current map bounds
    const propertiesInView = propertyData.filter((property) => {
      const lat = Number(property?.lat);
      const lng = Number(property?.lng);

      return (
        lat && lng && bounds.contains(new window.google.maps.LatLng(lat, lng))
      );
    });
    return propertiesInView.length;
  };

  const handleZoomChange = () => {
    const newZoom = mapRef?.current?.zoom;

    // trackMapEvent("MAP VIEW", "MAP VIEW EVENT")

    if (newZoom !== zoomValue) {
      setPrevZoom(zoomValue);
      setZoomValue(newZoom);

      // Calculate properties in viewport
      const propertiesInViewport = getPropertiesInViewProt();

      trackMapEvent(
        "map_zoom_changed",
        `Available properties: ${propertiesInViewport} | Current zoom: ${
          zoomValue || 0
        } | Updated zoom: ${prevZoom || 0}`,
        {
          property_available: propertiesInViewport,
          current_zoom: zoomValue || 0,
          updated_zoom: prevZoom || 0,
        }
      );
    }
  };

  const handleMapDragEnd = () => {

    // Update address bar URL with new map bounds
    const bounds = mapRef?.current?.getBounds();
    if (bounds) {
      const swLatLng = `${bounds.getSouthWest().lat()},${bounds.getSouthWest().lng()}`;
      const neLatLng = `${bounds.getNorthEast().lat()},${bounds.getNorthEast().lng()}`;
      const center = mapRef?.current?.getCenter();
      const centerPoint = `${center.lat()},${center.lng()}`;

      const params = {
        swLatLng,
        neLatLng,
        centerPoint,
        rect: true,
        zoomValue: mapRef?.current?.zoom
      };
      if (mapParams) {
        setMapParams(params);
        let addressBarURL = new URL(window.location.href);
        if (addressBarURL.searchParams.has("swLatLng")) {
          addressBarURL.searchParams.delete("swLatLng");
        }
        if (addressBarURL.searchParams.has("neLatLng")) {
          addressBarURL.searchParams.delete("neLatLng");
        }
        if (addressBarURL.searchParams.has("centerPoint")) {
          addressBarURL.searchParams.delete("centerPoint");
        }
        if (addressBarURL.searchParams.has("rect")) {
          addressBarURL.searchParams.delete("rect");
        }
        if (addressBarURL.searchParams.has("zoomValue")) {
          addressBarURL.searchParams.delete("zoomValue");
        }
        addressBarURL.searchParams.append("swLatLng", params?.swLatLng);
        addressBarURL.searchParams.append("neLatLng", params?.neLatLng);
        addressBarURL.searchParams.append("centerPoint", params?.centerPoint);
        addressBarURL.searchParams.append("rect", params?.rect);
        addressBarURL.searchParams.append("zoomValue", params?.zoomValue);
        history.pushState({}, "", addressBarURL.href);
        dispatch(addMapParam({...params, zoomValue: parseInt(params?.zoomValue)}));
      }
    }
  };

    // Handle map type change
  const handleMapTypeChange = () => {
    const newMapType = mapRef?.current?.getMapTypeId();
    if (newMapType && newMapType !== mapTypeId) {
      trackMapEvent(
        "map_type_changed",
        `From:${mapTypeId === "roadmap" ? "Map" : "Satellite"}| To:${
          newMapType === "roadmap" ? "Map" : "Satellite"
        }`,
        {
          from_type: mapTypeId === "roadmap" ? "map" : "satellite",
          to_type: newMapType === "roadmap" ? "map" : "satellite",
        }
      );
      setMapTypeId(newMapType);
    }
  };

  // Handle fullscreen change
  const handleFullscreenChange = () => {
    const isInFullScreen = !!(
      document.fullscreenElement ||
      document.webkitFullscreenElement ||
      document.mozFullScreenElement ||
      document.msFullscreenElement
    );
    if (isInFullScreen !== isFullScreen) {
      trackMapEvent(
        "map_fullscreen_toggle",
        isInFullScreen ? "Entered fullscreen" : "Exited fullscreen",
        { state: isInFullScreen ? "maximised" : "minimised" }
      );
      setIsFullScreen(isInFullScreen);
    }
  };

  // Add fullscreen event listener
  useEffect(() => {
    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => {
      document.removeEventListener('fullscreenchange', handleFullscreenChange);
    };
  }, [isFullScreen]);

  useEffect(() => {
    setPropertyData(srpTupplesMap);
  }, [srpTupplesMap]);

  useEffect(() => {
    if (selectedProperty && selectedProperty?.propertyId && selectedProperty?.scrollTo) {
      const element = document.getElementById(selectedProperty?.propertyId);
      if (element) {
        window?.scroll({
          top: element?.offsetTop,
          behavior: "smooth",
        })
      }
    }
  }, [selectedProperty?.propertyId]);

  useEffect(() => {
    if (mapParams?.zoomValue) {
      let addressBarURL = new URL(window.location.href);
      if (addressBarURL.searchParams.has("swLatLng")) {
        addressBarURL.searchParams.delete("swLatLng");
      }
      if (addressBarURL.searchParams.has("neLatLng")) {
        addressBarURL.searchParams.delete("neLatLng");
      }
      if (addressBarURL.searchParams.has("centerPoint")) {
        addressBarURL.searchParams.delete("centerPoint");
      }
      if (addressBarURL.searchParams.has("rect")) {
        addressBarURL.searchParams.delete("rect");
      }
      if (addressBarURL.searchParams.has("zoomValue")) {
        addressBarURL.searchParams.delete("zoomValue");
      }
      addressBarURL.searchParams.append("swLatLng", mapParams?.swLatLng);
      addressBarURL.searchParams.append("neLatLng", mapParams?.neLatLng);
      addressBarURL.searchParams.append("centerPoint", mapParams?.centerPoint);
      addressBarURL.searchParams.append("rect", mapParams?.rect);
      addressBarURL.searchParams.append("zoomValue", mapParams?.zoomValue);
      history.pushState({}, "", addressBarURL.href);
      dispatch(addMapParam({...mapParams, rect: (mapParams?.rect || mapParams?.rect === 'true') ? true : false, zoomValue: parseInt(mapParams?.zoomValue)}))
    }
  }, [mapParams?.zoomValue]);

  useEffect(() => {
    if (mapParams || mapParamRDX) {
      const map = mapRef?.current;
      const LatLngBounds = map?.getBounds();
      const center = map?.getCenter();
      if (LatLngBounds && center) {
        const params = {
          swLatLng: `${LatLngBounds.getSouthWest().lat()},${LatLngBounds.getSouthWest().lng()}`,
          neLatLng: `${LatLngBounds.getNorthEast().lat()},${LatLngBounds.getNorthEast().lng()}`,
          centerPoint: `${center.lat()},${center.lng()}`,
          rect: true,
          zoomValue,
        }
        setMapParams(params);
        dispatch(setSelectedProperty(null));
      }
    }
  }, [mapParams?.swLatLng, zoomValue, mapRef?.current]);

  useEffect(() => {
    if (!mapParamRDX && mapParams) {
      setMapParams('');
    }
  }, [mapParamRDX]);


  const setBoundaries = useCallback(() => {
    if (mapRef?.current && propertyData?.length > 0 && !mapParams?.swLatLng) {
      const LatLngBounds = new window.google.maps.LatLngBounds();
      propertyData.forEach((property) => {
        const lat = Number(property?.lat);
        const lng = Number(property?.lng);
        if (lat && lng) {
          LatLngBounds.extend({lat, lng});
        }
      });
      if (!LatLngBounds.isEmpty()) {
        mapRef.current.fitBounds(LatLngBounds);
      }
    }
  }, [propertyData, mapParams])

  useEffect(() => {
    setBoundaries();
  }, [propertyData]);

  const defaultCentroid = {lat: 38.7946, lng: -106.5348};
  const onResetPressed = () => {
    // Track reset event
    // trackMapEvent("Map reset", "Map view reset");

    let addressBarURL = new URL(window.location.href);
    if (addressBarURL.searchParams.has("swLatLng")) {
      addressBarURL.searchParams.delete("swLatLng");
    }
    if (addressBarURL.searchParams.has("neLatLng")) {
      addressBarURL.searchParams.delete("neLatLng");
    }
    if (addressBarURL.searchParams.has("centerPoint")) {
      addressBarURL.searchParams.delete("centerPoint");
    }
    if (addressBarURL.searchParams.has("rect")) {
      addressBarURL.searchParams.delete("rect");
    }
    if (addressBarURL.searchParams.has("zoomValue")) {
      addressBarURL.searchParams.delete("zoomValue");
    }
    history.pushState({}, "", addressBarURL.href);
    setMapParams("");
    dispatch(addMapParam(""));
  }

  const getPosition = (markerLat, markerLng) => {
    const map = mapRef?.current;
    const LatLngBounds = map?.getBounds();
    const center = map?.getCenter();
    if (!LatLngBounds || !center) {
      return { pixelOffset: new google.maps.Size(0, 0), arrow: '' };
    }
    let mapMinLng = LatLngBounds.getSouthWest().lng();
    let mapMaxLng = LatLngBounds.getNorthEast().lng();
    let mapMinLat = LatLngBounds.getSouthWest().lat();
    let mapMaxLat = LatLngBounds.getNorthEast().lat();
    let mapCenterLat = center?.lat();
    let mapCenterLng = center?.lng();
    if (markerLat < mapMaxLat && markerLat > mapCenterLat) {
      if ((mapMinLng + ((mapCenterLng - mapMinLng) / 3)) > markerLng) {
        return {pixelOffset: new google.maps.Size(212, 80), arrow: 'map-arrow-right'};
      } else if ((mapMaxLng + ((mapCenterLng - mapMaxLng) / 3)) < markerLng) {
        return {pixelOffset: new google.maps.Size(-212, 80), arrow: 'map-arrow-left'};
      } else if (markerLng > mapMinLng && (mapMinLng + ((mapCenterLng - mapMinLng) / 2)) > markerLng) {
        return {pixelOffset: new google.maps.Size(120, 160), arrow: 'map-arrow-down', arrowStyle: {left: 85}};
      } else if (markerLng > mapCenterLng && (mapCenterLng + ((mapMaxLng - mapCenterLng) / 2)) > markerLng) {
        return {pixelOffset: new google.maps.Size(-70, 160), arrow: 'map-arrow-down', arrowStyle: {left: 275}};
      } else if (markerLng > mapCenterLng) {
        return {pixelOffset: new google.maps.Size(-120, 160), arrow: 'map-arrow-down', arrowStyle: {left: 325}};
      } else {
        return {pixelOffset: new google.maps.Size(70, 160), arrow: 'map-arrow-down', arrowStyle: {left: 135}};
      }
    } else if (markerLat < mapCenterLat && markerLat > mapMinLat) {
      if ((mapMinLng + ((mapCenterLng - mapMinLng) / 3)) > markerLng) {
        return {pixelOffset: new google.maps.Size(212, 80), arrow: 'map-arrow-right'};
      } else if ((mapMaxLng + ((mapCenterLng - mapMaxLng) / 3)) < markerLng) {
        return {pixelOffset: new google.maps.Size(-212, 80), arrow: 'map-arrow-left'};
      } else if (markerLng > mapMinLng && (mapMinLng + ((mapCenterLng - mapMinLng) / 2)) > markerLng) {
        return {pixelOffset: new google.maps.Size(120, 0), arrow: 'map-arrow-up', arrowStyle: {left: 85}};
      } else if (markerLng > mapCenterLng && (mapCenterLng + ((mapMaxLng - mapCenterLng) / 2)) > markerLng) {
        return {pixelOffset: new google.maps.Size(-70, 0), arrow: 'map-arrow-up', arrowStyle: {left: 275}};
      } else if (markerLng > mapCenterLng) {
        return {pixelOffset: new google.maps.Size(-120, 0), arrow: 'map-arrow-up', arrowStyle: {left: 325}};
      } else {
        if (mapMaxLng < 0 && mapMinLng < 0 && mapMaxLat > 0 && mapMinLat > 0 && mapCenterLat > 0) {
          return {pixelOffset: new google.maps.Size(30, 0), arrow: 'map-arrow-up', arrowStyle: {left: 175}};
        } else {
        return {pixelOffset: new google.maps.Size(70, 0), arrow: 'map-arrow-up', arrowStyle: {left: 135}};
        }
      }
    }
  }

  const getInfoWindow = (selectedProperty) => {
    const WindowPosition = getPosition(Number(selectedProperty.lat), Number(selectedProperty.lng));
    return (
      <>
        <InfoWindow
          position={{
            lat: Number(selectedProperty.lat),
            lng: Number(selectedProperty.lng),
          }}
          onCloseClick={() => dispatch(setSelectedProperty(null))}
          options={{
            disableAutoPan: true,
            pixelOffset: WindowPosition?.pixelOffset || new google.maps.Size(0, 160)
          }}
        >
          <div onMouseLeave={() => dispatch(setSelectedProperty(null))}>
            <CardItemMap item={selectedProperty} setShowModal={setShowModal} />
            <div className={`map-tooltipArrow ${WindowPosition?.arrow || 'map-arrow-down'}`} style={WindowPosition?.arrowStyle ? WindowPosition?.arrowStyle : {}} />
          </div>
        </InfoWindow>
      </>
    );
  }

  return (
    <>
      {!isLoaded ? (
        <>
          <img
            style={{ height: '100%', width: '100%', position: 'absolute', zIndex: 1, marginTop: 0 }}
            src={map_placeholder}
          />
          <button type="button" className="cluster-loader-btn">
            <div className="cluster-loader"></div>
            <div className="cluster-loader-text">{!isLoaded ? 'Loading...' : 'Retrieving properties'}</div>
          </button>
        </>
      ) : (
        <GoogleMap
          mapContainerStyle={containerStyle}
          options={{
            zoomControl: true,
            minZoom: 2.0
          }}
          edgePadding={{ top: 0, left: 0, bottom: 0, right: 0 }}
          clusterColor={'#118BF4'}
          clusterTextColor={'#ffffff'}
          tracksViewChanges={true}
          onDragEnd={handleMapDragEnd}
          onZoomChanged={handleZoomChange}
          onMapTypeIdChanged={handleMapTypeChange}
          zoom={4.0}
          center={mapParams?.centerPoint || mapParamRDX?.centerPoint ? {lat: Number(mapParamRDX?.centerPoint?.split(',')[0]), lng: Number(mapParamRDX?.centerPoint?.split(',')[1])} : propertyData?.length > 0 ? {} : defaultCentroid}
          onLoad={(map) => {
            mapRef.current = map;
            setBoundaries();
          }}
        >
          {!isSRPMapLoading && propertyData?.length > 64 ? (
            <MapClusters
              propertyData={propertyData}
              setPropertyData={setPropertyData}
              setMapParams={setMapParams}
            />
            ) : !isSRPMapLoading && propertyData?.length > 0 && (
              propertyData?.length > 0 && propertyData?.map((marker, index) => {
                let isSaved = false;
                srpTupples?.length > 0 && srpTupples?.map(tupple => {
                  if (tupple?.lat === marker?.lat && tupple?.lng === marker?.lng && tupple?.onWatchList === "Y") {
                    isSaved = true;
                    marker = tupple;
                  }
                });
                return (
                  <MarkerF
                    key={`marker-${marker.lat}-${marker.lng}-${index}`}
                    position={{
                      lat: Number(marker.lat),
                      lng: Number(marker.lng),
                    }}
                    tracksViewChanges={true}
                    icon={{
                      url: transparent,
                      scaledSize: new window.google.maps.Size(18, 18),
                    }}
                    zIndex={isSaved ? 100000 : 0}
                    label={{text: ' ', className: isSaved ? 'map-circle-icon' : 'map-circle'}}
                    onMouseOver={() => {
                      if (selectedProperty?.lat !== marker?.lat && selectedProperty?.lng !== marker.lng) {
                        dispatch(setSelectedProperty({...marker, scrollTo: true}))
                        // trackMapEvent("Map marker hover", "Map marker hover");
                      }
                    }}
                    onClick={() => {
                      // trackMapEvent("Map marker click", "Map marker click");
                      if (selectedProperty) {
                        dispatch(setSelectedProperty(null))
                      } else if (selectedProperty?.lat !== marker?.lat && selectedProperty?.lng !== marker.lng) {
                        dispatch(setSelectedProperty({...marker, scrollTo: true}))
                      }
                    }}
                  >
                  </MarkerF>
                );
              }
            ))
          }
          {!isSRPMapLoading && propertyData?.length > 0 && selectedProperty !== null && propertyData?.length < 64 && getInfoWindow(selectedProperty)}
          <FavoriteModal item={selectedProperty} isMapCard={true} showModal={showModal} handleClose={() => setShowModal(false)} setShowModal={setShowModal}/>
          {isSRPMapLoading && (
            <>
              {!mapParams && (<img
                style={{ height: '100%', width: '100%', position: 'absolute', zIndex: 1, marginTop: 0 }}
                src={map_placeholder}
              />)}
              <button type="button" className="cluster-loader-btn">
                <div className="cluster-loader"></div>
                <div className="cluster-loader-text">{!isLoaded ? 'Loading...' : 'Retrieving properties'}</div>
              </button>
            </>
          )}
          {mapParams && mapParamRDX && <button type="button" className={`reset-map-search`} onClick={onResetPressed}>Reset</button>}
        </GoogleMap>
      )}
    </>
  );
}
